/**
 * "Added to wishlist" notification
 * - Listens for 'wishlist:added' (dispatched by toggleFavorite in theme.js)
 * - Never closes by itself; only the close button removes a card
 * - Desktop: cards stack, max 3 - adding a 4th removes the oldest
 * - Mobile: a new card replaces the open one
 * - Nothing is persisted, so all cards are gone after navigating to another page
 */
(function () {
  const container = document.querySelector('.wishlist-notifications');
  if (!container) return;

  const MAX_DESKTOP = 3;
  const mobileQuery = window.matchMedia('(max-width: 767px)');
  const { wishlistUrl, iconHeart, iconClose, labelAdded, labelView, labelClose } = container.dataset;

  const updateOpenState = () => {
    document.body.classList.toggle('wishlist-notification-open', container.children.length > 0);
  };

  // Product image: from the card / product page that was clicked, else the product JSON
  const findImage = (button) => {
    const scope = button?.closest('.card-product, .card-product-wrapper') || (button?.closest('.product') && document.querySelector('.product__media'));
    const img = scope?.querySelector('img');
    return img ? img.currentSrc || img.src : null;
  };

  const fetchProduct = async (handle) => {
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}products/${encodeURIComponent(handle)}.js`);
      return response.ok ? await response.json() : null;
    } catch (e) {
      return null;
    }
  };

  const removeCard = (card) => {
    card.classList.remove('is-visible');
    card.addEventListener('transitionend', () => {
      card.remove();
      updateOpenState();
    }, { once: true });
    // Fallback if no transition runs (e.g. reduced motion)
    setTimeout(() => {
      if (card.isConnected) {
        card.remove();
        updateOpenState();
      }
    }, 400);
  };

  const buildCard = (handle, title, imageUrl) => {
    const card = document.createElement('div');
    card.className = 'wishlist-notification';
    card.dataset.handle = handle;
    card.setAttribute('role', 'status');
    card.innerHTML = `
      <div class="wishlist-notification__media"><img alt="" loading="eager"></div>
      <div class="wishlist-notification__content">
        <p class="wishlist-notification__status"><img src="${iconHeart}" alt="" width="14" height="14"><span></span></p>
        <p class="wishlist-notification__title"></p>
        <a class="wishlist-notification__link"></a>
      </div>
      <button type="button" class="wishlist-notification__close" aria-label="${labelClose}">
        <img src="${iconClose}" alt="" width="18" height="18">
      </button>`;
    // Texts come from theme settings / product data, so set them as text
    card.querySelector('.wishlist-notification__status span').textContent = labelAdded;
    card.querySelector('.wishlist-notification__title').textContent = title;
    const link = card.querySelector('.wishlist-notification__link');
    link.href = wishlistUrl;
    link.textContent = labelView;
    const img = card.querySelector('.wishlist-notification__media img');
    if (imageUrl) img.src = imageUrl;
    else card.querySelector('.wishlist-notification__media').hidden = true;
    card.querySelector('.wishlist-notification__close').addEventListener('click', () => removeCard(card));
    return card;
  };

  const show = (card) => {
    if (mobileQuery.matches) {
      // Mobile: replace whatever is open
      [...container.children].forEach((old) => old.remove());
    } else {
      // Desktop: keep at most 3, dropping the oldest
      while (container.children.length >= MAX_DESKTOP) container.firstElementChild.remove();
    }
    container.appendChild(card);
    updateOpenState();
    requestAnimationFrame(() => requestAnimationFrame(() => card.classList.add('is-visible')));
  };

  document.addEventListener('wishlist:added', async (e) => {
    const { handle, title, button } = e.detail || {};
    if (!handle) return;

    let imageUrl = findImage(button);
    let productTitle = title;
    if (!imageUrl || !productTitle) {
      const product = await fetchProduct(handle);
      imageUrl = imageUrl || product?.featured_image || null;
      productTitle = productTitle || product?.title || handle;
    }

    show(buildCard(handle, productTitle, imageUrl));
  });

  // Product taken off the wishlist while its notification is still open
  document.addEventListener('wishlist:removed', (e) => {
    const handle = e.detail?.handle;
    if (!handle) return;
    container.querySelectorAll('.wishlist-notification').forEach((card) => {
      if (card.dataset.handle === handle) removeCard(card);
    });
  });

  // Navigating away clears everything - including when the browser restores this
  // page from its back/forward cache, which would otherwise bring the cards back
  const clearAll = () => {
    container.replaceChildren();
    updateOpenState();
  };
  window.addEventListener('pagehide', clearAll);
  window.addEventListener('pageshow', (e) => { if (e.persisted) clearAll(); });
})();
